import {
    Box, Grid, IconButton, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow,
    Tabs, Tab, Tooltip, Typography
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import UserActivityLogo from "../../assets/Images/userActivityWhiteLogo.png"
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { useLocation, useNavigate } from 'react-router-dom'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import {
    PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer, Legend
} from 'recharts'
import { ApiGetUserActivity } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'


const fmt = (n: number) =>
    (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const fmtTime = (d: string) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const DEFAULT_PAGE_LIMIT = 5

const statusMap: Record<string, { color: string; bg: string; border: string }> = {
    COMPLETED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    PENDING: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    PROCESSING: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    FAILED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    CANCELLED: { color: '#9ca3af', bg: '#9ca3af18', border: '#9ca3af50' },
    INITIATED: { color: '#a78bfa', bg: '#a78bfa18', border: '#a78bfa50' },
    BUY: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    SELL: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    CASHOUT: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    DIRECT: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    LOAN: { color: '#a78bfa', bg: '#a78bfa18', border: '#a78bfa50' },
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const s = statusMap[status] || { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50' }
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {status}
        </Box>
    )
}


const StatCard: React.FC<{
    label: string; value: string; sub?: string; color: string; icon: React.ReactNode
}> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333', borderRadius: '14px', p: 2.5,
        position: 'relative', overflow: 'hidden', transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">
                {label}
            </Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>
            {value}
        </Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const PortfolioPieChart: React.FC<{ portfolio: any }> = ({ portfolio }) => {
    if (!portfolio) return null

    const raw = [
        { name: 'USD Cash', value: portfolio.availableFiatBalance, color: '#10b981' },
        { name: 'Crypto', value: portfolio.totalCryptoValue, color: '#f2674a' },
        { name: 'Stocks', value: portfolio.totalStockValue, color: '#3b82f6' },
        { name: 'ETF', value: portfolio.totalETFValue, color: '#a78bfa' },
    ].filter(d => d.value > 0)

    if (raw.length === 0) return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
            <Typography fontSize={12} color="#4a4a4a">No portfolio data</Typography>
        </Box>
    )

    const total = raw.reduce((s, d) => s + d.value, 0)

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload?.length) return null
        const d = payload[0]
        return (
            <Box sx={{
                background: '#1e1e1e', border: '1px solid #333', borderRadius: '10px',
                px: 2, py: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}>
                <Typography fontSize={11} color="#6f6f6f" mb={0.5}>{d.name}</Typography>
                <Typography fontSize={13} fontWeight={700} color={d.payload.color}>
                    ${fmt(d.value)}
                </Typography>
                <Typography fontSize={11} color="#5a5a5a">
                    {((d.value / total) * 100).toFixed(1)}% of portfolio
                </Typography>
            </Box>
        )
    }

    const CustomLabel = ({ cx, cy }: any) => (
        <>
            <text x={cx} y={cy - 8} textAnchor="middle" fill="#9a9a9a" fontSize={11}>Total</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fill="#ffffff" fontSize={15} fontWeight={700}>
                ${fmt(total)}
            </text>
        </>
    )

    return (
        <Box>
            <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                    <Pie
                        data={raw}
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        labelLine={false}
                        label={CustomLabel}
                    >
                        {raw.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.color}
                                stroke="transparent"
                            />
                        ))}
                    </Pie>
                    <ReTooltip content={<CustomTooltip />} />
                    <Legend
                        formatter={(value, entry: any) => (
                            <span style={{ color: '#9a9a9a', fontSize: 11 }}>
                                {value} <span style={{ color: entry.payload.color, fontWeight: 700 }}>
                                    ${fmt(entry.payload.value)}
                                </span>
                            </span>
                        )}
                        wrapperStyle={{ paddingTop: 12 }}
                    />
                </PieChart>
            </ResponsiveContainer>

            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {[
                    { label: 'Total P&L', value: `$${fmt(portfolio.totalPnL)}`, color: portfolio.totalPnL >= 0 ? '#10b981' : '#ef4444' },
                    { label: 'P&L %', value: `${portfolio.totalPnLPercent}%`, color: portfolio.totalPnLPercent >= 0 ? '#10b981' : '#ef4444' },
                    { label: 'Withdrawable', value: `$${fmt(portfolio.withdrawableFiatBalance)}`, color: '#3b82f6' },
                ].map(({ label, value, color }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                        <Typography fontSize={11} color="#6f6f6f">{label}</Typography>
                        <Typography fontSize={12} fontWeight={700} color={color}>{value}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

const AssetPieChart: React.FC<{ assets: any[] }> = ({ assets }) => {
    if (!assets || assets.length === 0) return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
            <Typography fontSize={12} color="#4a4a4a">No asset data</Typography>
        </Box>
    )

    const COLORS = ['#f2674a', '#3b82f6', '#a78bfa', '#f59e0b', '#10b981', '#ec4899', '#22d3ee', '#84cc16', '#fb923c', '#e879f9']

    const pieData = assets
        .filter(a => a.assetType !== 'FIAT' && a.balance > 0)
        .map((asset, i) => ({
            name: asset.assetName || asset.currency.toUpperCase(),
            symbol: asset.currency.toUpperCase(),
            value: Number((asset.currentMarketValue || 0).toFixed(2)),
            rawBalance: asset.balance,
            pnlPercent: asset.pnlPercent ?? 0,
            totalPnL: asset.totalPnL ?? 0,
            color: COLORS[i % COLORS.length]
        }))
        .filter(d => d.value > 0)

    if (pieData.length === 0) return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
            <Typography fontSize={12} color="#4a4a4a">No investment assets</Typography>
        </Box>
    )

    const total = pieData.reduce((s, d) => s + d.value, 0)

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload?.length) return null
        const d = payload[0].payload
        return (
            <Box sx={{
                background: '#1e1e1e', border: '1px solid #333', borderRadius: '10px',
                px: 2, py: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}>
                <Typography fontSize={12} fontWeight={700} color={d.color} mb={0.3}>{d.name}</Typography>
                <Typography fontSize={11} color="#9a9a9a">
                    {d.rawBalance.toFixed(6)} {d.symbol}
                </Typography>
                <Typography fontSize={12} fontWeight={700} color="#fff">
                    ≈ ${fmt(d.value)}
                </Typography>
                <Typography fontSize={11} color={d.pnlPercent >= 0 ? '#10b981' : '#ef4444'}>
                    P&L: {d.pnlPercent >= 0 ? '+' : ''}{d.pnlPercent}% (${fmt(d.totalPnL)})
                </Typography>
                <Typography fontSize={10} color="#5a5a5a">
                    {((d.value / total) * 100).toFixed(1)}% of portfolio
                </Typography>
            </Box>
        )
    }

    const CustomLabel = ({ cx, cy }: any) => (
        <>
            <text x={cx} y={cy - 8} textAnchor="middle" fill="#9a9a9a" fontSize={10}>Current</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fill="#ffffff" fontSize={13} fontWeight={700}>
                ${fmt(total)}
            </text>
        </>
    )

    return (
        <ResponsiveContainer width="100%" height={220}>
            <PieChart>
                <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    labelLine={false}
                    label={CustomLabel}
                >
                    {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent" />
                    ))}
                </Pie>
                <ReTooltip content={<CustomTooltip />} />
                <Legend
                    formatter={(value, entry: any) => (
                        <span style={{ color: '#9a9a9a', fontSize: 10 }}>
                            {value}{' '}
                            <span style={{ color: entry.payload.color, fontWeight: 700 }}>
                                ${fmt(entry.payload.value)}
                            </span>
                        </span>
                    )}
                    wrapperStyle={{ paddingTop: 8 }}
                />
            </PieChart>
        </ResponsiveContainer>
    )
}

const AssetBreakdownChart: React.FC<{ assets: any[] }> = ({ assets }) => {
    if (!assets || assets.length === 0) return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
            <Typography fontSize={12} color="#4a4a4a">No asset data</Typography>
        </Box>
    )

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            <AssetPieChart assets={assets} />
        </Box>
    )
}

const thStyle = {
    backgroundColor: '#241815', color: '#ff6a3d', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const, py: 1.5, borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const, top: 0, zIndex: 2,
}
const tdStyle = { color: '#c0c0c0', py: 1.4, fontSize: 13, whiteSpace: 'nowrap' as const }
const trHover = { transition: 'background 0.15s', '&:hover': { background: '#ff6a3d08' } }

const UserActivity: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const userId = location.state?.userId
    const userName = location.state?.userName || 'User'

    type TableKey = 'DEPOSIT' | 'WITHDRAWAL' | 'TRADE' | 'REPAYMENT'
    interface TableState {
        rows: any[]
        total: number
        page: number
        rowsPerPage: number
    }

    const initialTableState = (): TableState => ({
        rows: [], total: 0, page: 0, rowsPerPage: DEFAULT_PAGE_LIMIT
    })

    const [tab, setTab] = useState(0)
    const [summary, setSummary] = useState<any>(null)
    const [chartData, setChartData] = useState<any[]>([])
    const [portfolio, setPortfolio] = useState<any>(null)
    const [assetBreakdown, setAssetBreakdown] = useState<any[]>([])
    const [errorPopup, setErrorPopup] = useState({ open: false, message: '' })
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('user-activity','all')

    const [depositState, setDepositState] = useState<TableState>(initialTableState)
    const [withdrawalState, setWithdrawalState] = useState<TableState>(initialTableState)
    const [tradeState, setTradeState] = useState<TableState>(initialTableState)
    const [repaymentState, setRepaymentState] = useState<TableState>(initialTableState)

    const tabToType: TableKey[] = ['DEPOSIT', 'WITHDRAWAL', 'TRADE', 'REPAYMENT']

    const stateSetters = useMemo<Record<TableKey, React.Dispatch<React.SetStateAction<TableState>>>>(() => ({
        DEPOSIT: setDepositState,
        WITHDRAWAL: setWithdrawalState,
        TRADE: setTradeState,
        REPAYMENT: setRepaymentState,
    }), [])
    const tableStates: Record<TableKey, TableState> = {
        DEPOSIT: depositState,
        WITHDRAWAL: withdrawalState,
        TRADE: tradeState,
        REPAYMENT: repaymentState,
    }

    const requestTokens = useRef<Record<TableKey, number>>({ DEPOSIT: 0, WITHDRAWAL: 0, TRADE: 0, REPAYMENT: 0 })

    const fetchInitial = useCallback(async () => {
        if (!userId) return
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return

            const params = new URLSearchParams()
            params.append('userId', userId)
            params.append('type', 'ALL')
            params.append('page', '1')
            params.append('limit', String(DEFAULT_PAGE_LIMIT))
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }

            const res = await ApiGetUserActivity(`?${params.toString()}`)
            if (res?.status) {
                const d = res.data
                setSummary(d.summary)
                setChartData(d.chartData || [])
                setPortfolio(d.portfolio)
                setAssetBreakdown(d.assetBreakdown || [])

                setDepositState(s => ({
                    ...s, rows: d.tables?.deposits || [],
                    total: d.pagination?.deposits?.total || 0, page: 0
                }))
                setWithdrawalState(s => ({
                    ...s, rows: d.tables?.withdrawals || [],
                    total: d.pagination?.withdrawals?.total || 0, page: 0
                }))
                setTradeState(s => ({
                    ...s, rows: d.tables?.orders || [],
                    total: d.pagination?.orders?.total || 0, page: 0
                }))
                setRepaymentState(s => ({
                    ...s, rows: d.tables?.repayments || [],
                    total: d.pagination?.repayments?.total || 0, page: 0
                }))
            }
        } catch (e: any) {
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || 'Failed to fetch user activity',
            })
        }
    }, [userId, buildQuery])

    const fetchTable = useCallback(async (type: TableKey, page: number, rowsPerPage: number) => {
        if (!userId) return

        const dateQuery = buildQuery()
        if (dateQuery === null) return

        const myToken = ++requestTokens.current[type]
        stateSetters[type](s => ({ ...s, loading: true }))

        try {
            const params = new URLSearchParams()
            params.append('userId', userId)
            params.append('type', type)
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }

            const res = await ApiGetUserActivity(`?${params.toString()}`)
            if (myToken !== requestTokens.current[type]) return

            if (res?.status) {
                const d = res.data
                const key = type === 'DEPOSIT' ? 'deposits'
                    : type === 'WITHDRAWAL' ? 'withdrawals'
                        : type === 'TRADE' ? 'orders'
                            : 'repayments'
                stateSetters[type](s => ({
                    ...s,
                    rows: d.tables?.[key] || [],
                    total: d.pagination?.[key]?.total ?? s.total,
                    page, rowsPerPage,
                    loading: false,
                }))
            } else {
                stateSetters[type](s => ({ ...s, loading: false }))
            }
        } catch (e: any) {
            if (myToken !== requestTokens.current[type]) return
            stateSetters[type](s => ({ ...s, loading: false }))
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || `Failed to fetch ${type.toLowerCase()} data`,
            })
        }
    }, [userId, stateSetters, buildQuery])

    useEffect(() => { fetchInitial() }, [fetchInitial])

    const depositRows = depositState.rows
    const withdrawalRows = withdrawalState.rows
    const tradeRows = tradeState.rows

    const handlePageChange = (type: TableKey) => (_: unknown, newPage: number) => {
        fetchTable(type, newPage, tableStates[type].rowsPerPage)
    }

    const handleRowsPerPageChange = (type: TableKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newLimit = parseInt(e.target.value, 10)
        fetchTable(type, 0, newLimit)
    }

    const handleTabChange = (_: unknown, newTab: number) => {
        setTab(newTab)
        const type = tabToType[newTab]
        if (tableStates[type].rows.length === 0 && tableStates[type].total > 0) {
            fetchTable(type, tableStates[type].page, tableStates[type].rowsPerPage)
        }
    }

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <IconButton
                        sx={{ ':hover': { backgroundColor: '#ffffff1A' } }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIcon sx={{ color: '#ffffff' }} />
                    </IconButton>
                    <Box className="p-2 bg-light-white border-10">
                        <img src={UserActivityLogo} height={"30px"} alt="User Activity" />
                    </Box>
                    <Box>
                        <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>
                            User Activity
                        </Typography>
                        <Typography fontSize={12} color="#ffffff80">{userName}</Typography>
                    </Box>
                </Box>
            </Box>

            <>
                <Grid container spacing={2} mb={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Total Deposited"
                            value={`$${fmt(summary?.deposits?.completedAmount)}`}
                            sub={`${summary?.deposits?.completedCount} completed · ${summary?.deposits?.pendingCount} pending`}
                            color="#10b981"
                            icon={<ArrowDownwardIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Total Withdrawn"
                            value={`$${fmt(summary?.withdrawals?.totalAmount)}`}
                            sub={`${summary?.withdrawals?.count} completed`}
                            color="#ef4444"
                            icon={<ArrowUpwardIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Trade Volume"
                            value={`$${fmt(summary?.trades?.totalTradeVolume)}`}
                            sub={`${summary?.trades?.completedCount} completed trades`}
                            color="#f2674a"
                            icon={<SwapHorizIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Net Balance"
                            value={`$${fmt(summary?.netBalance)}`}
                            sub="Deposits minus withdrawals"
                            color="#a78bfa"
                            icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Total Buys"
                            value={`$${fmt(summary?.trades?.totalBuyAmount)}`}
                            sub={`${summary?.trades?.buyCount} buy orders`}
                            color="#10b981"
                            icon={<TrendingUpIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Total Sells"
                            value={`$${fmt(summary?.trades?.totalSellAmount)}`}
                            sub={`${summary?.trades?.sellCount} sell orders`}
                            color="#ef4444"
                            icon={<TrendingDownIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Pending Deposits"
                            value={`$${fmt(summary?.deposits?.pendingAmount)}`}
                            sub={`${summary?.deposits?.pendingCount} awaiting`}
                            color="#f59e0b"
                            icon={<ArrowDownwardIcon fontSize="small" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatCard
                            label="Total Fees Paid"
                            value={`$${fmt(summary?.trades?.totalFees)}`}
                            sub="Platform + provider fees"
                            color="#3b82f6"
                            icon={<AttachMoneyIcon fontSize="small" />}
                        />
                    </Grid>
                </Grid>

                {chartData.length > 0 && (
                    <Grid container spacing={2} mb={3}>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #2a2a2a, #222)',
                                border: '1px solid #333', borderRadius: '14px', p: 2.5, height: '100%'
                            }}>
                                <Typography fontSize={13} fontWeight={700} color="#9a9a9a" mb={2}
                                    letterSpacing="0.06em" textTransform="uppercase">
                                    Portfolio Breakdown
                                </Typography>
                                <PortfolioPieChart portfolio={portfolio} />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #2a2a2a, #222)',
                                border: '1px solid #333', borderRadius: '14px', p: 2.5, height: '100%'
                            }}>
                                <Typography fontSize={13} fontWeight={700} color="#9a9a9a" mb={2}
                                    letterSpacing="0.06em" textTransform="uppercase">
                                    Asset Breakdown
                                </Typography>
                                <AssetBreakdownChart assets={assetBreakdown || []} />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #2a2a2a, #222)',
                                border: '1px solid #333', borderRadius: '14px', p: 2.5,
                                height: '100%', display: 'flex', flexDirection: 'column',
                            }}>
                                <Typography fontSize={13} fontWeight={700} color="#9a9a9a" mb={2}
                                    letterSpacing="0.06em" textTransform="uppercase">
                                    Buy vs Sell Volume
                                </Typography>
                                <Box sx={{ flex: 1, minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>

                                    {(() => {
                                        const buyTotal = chartData?.find((d: any) => d.label === 'Buys')?.total || 0
                                        const sellTotal = chartData?.find((d: any) => d.label === 'Sells')?.total || 0
                                        const buyCount = chartData?.find((d: any) => d.label === 'Buys')?.count || 0
                                        const sellCount = chartData?.find((d: any) => d.label === 'Sells')?.count || 0
                                        const grandTotal = buyTotal + sellTotal || 1
                                        const buyPct = (buyTotal / grandTotal) * 100
                                        const sellPct = (sellTotal / grandTotal) * 100

                                        return (
                                            <>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                                                            <Typography fontSize={12} color="#9a9a9a">Buys</Typography>
                                                            <Typography fontSize={10} color="#aa9999">({buyCount} orders)</Typography>
                                                        </Box>
                                                        <Typography fontSize={13} fontWeight={700} color="#10b981">${fmt(buyTotal)}</Typography>
                                                    </Box>
                                                    <Box sx={{ height: 6, borderRadius: '4px', background: '#1e1e1e', overflow: 'hidden' }}>
                                                        <Box sx={{ width: `${buyPct}%`, height: '100%', background: '#10b981', borderRadius: '4px', transition: 'width 0.4s' }} />
                                                    </Box>
                                                    <Typography fontSize={10} color="#aa9999" textAlign="right">{buyPct.toFixed(1)}% of volume</Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                                                            <Typography fontSize={12} color="#9a9a9a">Sells</Typography>
                                                            <Typography fontSize={10} color="#aa9999">({sellCount} orders)</Typography>
                                                        </Box>
                                                        <Typography fontSize={13} fontWeight={700} color="#ef4444">${fmt(sellTotal)}</Typography>
                                                    </Box>
                                                    <Box sx={{ height: 6, borderRadius: '4px', background: '#1e1e1e', overflow: 'hidden' }}>
                                                        <Box sx={{ width: `${sellPct}%`, height: '100%', background: '#ef4444', borderRadius: '4px', transition: 'width 0.4s' }} />
                                                    </Box>
                                                    <Typography fontSize={10} color="#aa9999" textAlign="right">{sellPct.toFixed(1)}% of volume</Typography>
                                                </Box>

                                                <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid #222', display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography fontSize={11} color="#6f6f6f">Total Volume</Typography>
                                                    <Typography fontSize={13} fontWeight={700} color="#f2674a">${fmt(grandTotal)}</Typography>
                                                </Box>
                                            </>
                                        )
                                    })()}

                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                )}

                <DateRangeFilter
                    range={range}
                    onRangeChange={handleRangeChange}
                    customStart={customStart}
                    onCustomStartChange={setCustomStart}
                    customEnd={customEnd}
                    onCustomEndChange={setCustomEnd}
                />

                <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e' }}>
                        <Tabs
                            value={tab}
                            onChange={handleTabChange}
                            sx={{
                                minHeight: 40, background: '#1e1e1e', borderRadius: '10px', width: 'fit-content',
                                p: 0.5, border: '1px solid #333',
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTab-root': {
                                    minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                    color: '#6f6f6f', textTransform: 'none', px: 3, py: 0.5,
                                    transition: 'all 0.2s',
                                },
                                '& .Mui-selected': {
                                    color: '#fff !important',
                                    background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                    boxShadow: '0 2px 8px rgba(242,103,74,0.4)',
                                },
                            }}
                        >
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowDownwardIcon sx={{ fontSize: 15 }} /> Deposits
                                    <Box sx={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                        {depositState.total}
                                    </Box>
                                </Box>
                            } />
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowUpwardIcon sx={{ fontSize: 15 }} /> Withdrawals
                                    <Box sx={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                        {withdrawalState.total}
                                    </Box>
                                </Box>
                            } />
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SwapHorizIcon sx={{ fontSize: 15 }} /> Trades
                                    <Box sx={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                        {tradeState.total}
                                    </Box>
                                </Box>
                            } />
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalanceIcon sx={{ fontSize: 15 }} /> Repayments
                                    <Box sx={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                        {repaymentState.total}
                                    </Box>
                                </Box>
                            } />
                        </Tabs>

                    </Box>

                    {tab === 0 && (
                        <TableContainer sx={{ maxHeight: '55vh' }}>
                            <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                                <TableHead>
                                    <TableRow>
                                        {['#', 'Transaction ID', 'Date & Time', 'Amount', 'Net Amount', 'Provider', 'Fees', 'Status'].map(h => (
                                            <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {depositRows.length > 0 ? depositRows.map((tx: any, i: number) => (
                                        <TableRow key={tx._id} sx={trHover}>
                                            <TableCell sx={tdStyle}>{depositState.page * depositState.rowsPerPage + i + 1}</TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Tooltip title={tx.transactionId} arrow>
                                                    <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {tx.transactionId}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.createdAt)}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={14} fontWeight={700} color="#10b981">
                                                    +${fmt(tx.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#9a9a9a">${fmt(tx.netAmount)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#3b82f615', border: '1px solid #3b82f630' }}>
                                                    {tx.paymentProvider}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {tx.fees?.total > 0
                                                    ? <Tooltip title={`Platform: $${fmt(tx.fees.platform)} | Provider: $${fmt(tx.fees.provider)}`} arrow>
                                                        <Typography fontSize={12} color="#9a9a9a">${fmt(tx.fees.total)}</Typography>
                                                    </Tooltip>
                                                    : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                                }
                                            </TableCell>
                                            <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                                <ArrowDownwardIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                                <Typography color="#4a4a4a" fontSize={13}>No deposits found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {tab === 1 && (
                        <TableContainer sx={{ maxHeight: '55vh' }}>
                            <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                                <TableHead>
                                    <TableRow>
                                        {['#', 'Transaction ID', 'Date & Time', 'Amount', 'Net Amount', 'Provider', 'Bank', 'Fees', 'Status'].map(h => (
                                            <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {withdrawalRows.length > 0 ? withdrawalRows.map((tx: any, i: number) => (
                                        <TableRow key={tx._id} sx={trHover}>
                                            <TableCell sx={tdStyle}>{withdrawalState.page * withdrawalState.rowsPerPage + i + 1}</TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Tooltip title={tx.transactionId} arrow>
                                                    <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {tx.transactionId}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.createdAt)}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={14} fontWeight={700} color="#ef4444">
                                                    -${fmt(tx.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#9a9a9a">${fmt(tx.netAmount)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#3b82f615', border: '1px solid #3b82f630' }}>
                                                    {tx.paymentProvider}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {tx.bankDetails ? (
                                                    <Box>
                                                        <Typography fontSize={12} color="#9a9a9a" fontWeight={600}>{tx.bankDetails.bankName || '—'}</Typography>
                                                        <Typography fontSize={10} color="#5a5a5a" sx={{ fontFamily: 'monospace' }}>{tx.bankDetails.maskedAccountNumber || '****'}</Typography>
                                                    </Box>
                                                ) : <Typography fontSize={11} color="#3a3a3a">—</Typography>}
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {tx.fees?.total > 0
                                                    ? <Typography fontSize={12} color="#9a9a9a">${fmt(tx.fees.total)}</Typography>
                                                    : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                                }
                                            </TableCell>
                                            <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                                <ArrowUpwardIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                                <Typography color="#4a4a4a" fontSize={13}>No withdrawals found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {tab === 2 && (
                        <TableContainer sx={{ maxHeight: '55vh' }}>
                            <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                                <TableHead>
                                    <TableRow>
                                        {['#', 'Order ID', 'Date & Time', 'Pair', 'Side', 'Type', 'Filled Quantity', 'Amount', 'Filled', 'Avg Price', 'Fees', 'Status'].map(h => (<TableCell key={h} sx={thStyle}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tradeRows.length > 0 ? tradeRows.map((o: any, i: number) => (
                                        <TableRow key={o._id} sx={trHover}>
                                            <TableCell sx={tdStyle}>{tradeState.page * tradeState.rowsPerPage + i + 1}</TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Tooltip title={o.clientOrderId} arrow>
                                                    <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {o.clientOrderId}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#e0e0e0">{fmtDate(o.createdAt)}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{fmtTime(o.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={12} fontWeight={700} color="#a78bfa" sx={{ fontFamily: 'monospace' }}>
                                                    {o.currencyPair}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}><StatusPill status={o.side} /></TableCell>
                                            <TableCell sx={tdStyle}><StatusPill status={o.type} /></TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#e0e0e0">
                                                    {o.filledQuantity != null ? o.filledQuantity.toFixed(8) : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} fontWeight={700} color={o.side === 'BUY' ? '#10b981' : '#ef4444'}>
                                                    ${fmt(o.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#9a9a9a">
                                                    {o.filledAmount != null ? `$${fmt(o.filledAmount)}` : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={12} color="#9a9a9a">
                                                    {o.avgPrice != null ? `$${fmt(o.avgPrice)}` : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {(o.platformFee || o.fees)
                                                    ? <Tooltip title={`Platform: $${fmt(o.platformFee)} | Provider: $${fmt(o.fees)}`} arrow>
                                                        <Typography fontSize={12} color="#9a9a9a">${fmt((o.platformFee || 0) + (o.fees || 0))}</Typography>
                                                    </Tooltip>
                                                    : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                                }
                                            </TableCell>
                                            <TableCell sx={tdStyle}><StatusPill status={o.status} /></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                                <SwapHorizIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                                <Typography color="#4a4a4a" fontSize={13}>No trades found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {tab === 3 && (
                        <TableContainer sx={{ maxHeight: '55vh' }}>
                            <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                                <TableHead>
                                    <TableRow>
                                        {['#', 'Transaction ID', 'Loan ID', 'Date & Time', 'Amount', 'Fees', 'Status'].map(h => (
                                            <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {repaymentState.rows.length > 0 ? repaymentState.rows.map((tx: any, i: number) => (
                                        <TableRow key={tx._id} sx={trHover}>
                                            <TableCell sx={tdStyle}>{repaymentState.page * repaymentState.rowsPerPage + i + 1}</TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Tooltip title={tx.transactionId} arrow>
                                                    <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {tx.transactionId}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace' }}>{tx.loanId || '—'}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.createdAt)}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={14} fontWeight={700} color="#a78bfa">${fmt(tx.amount)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {tx.fees?.total > 0
                                                    ? <Typography fontSize={12} color="#9a9a9a">${fmt(tx.fees.total)}</Typography>
                                                    : <Typography fontSize={11} color="#3a3a3a">—</Typography>}
                                            </TableCell>
                                            <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                                <AccountBalanceIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                                <Typography color="#4a4a4a" fontSize={13}>No repayments found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {tab === 0 && (
                        <TablePagination
                            component="div" count={depositState.total}
                            page={depositState.page} onPageChange={handlePageChange('DEPOSIT')}
                            rowsPerPage={depositState.rowsPerPage} onRowsPerPageChange={handleRowsPerPageChange('DEPOSIT')}
                            rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton sx={paginationStyle}
                        />
                    )}
                    {tab === 1 && (
                        <TablePagination
                            component="div" count={withdrawalState.total}
                            page={withdrawalState.page} onPageChange={handlePageChange('WITHDRAWAL')}
                            rowsPerPage={withdrawalState.rowsPerPage} onRowsPerPageChange={handleRowsPerPageChange('WITHDRAWAL')}
                            rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton sx={paginationStyle}
                        />
                    )}
                    {tab === 2 && (
                        <TablePagination
                            component="div" count={tradeState.total}
                            page={tradeState.page} onPageChange={handlePageChange('TRADE')}
                            rowsPerPage={tradeState.rowsPerPage} onRowsPerPageChange={handleRowsPerPageChange('TRADE')}
                            rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton sx={paginationStyle}
                        />
                    )}
                    {tab === 3 && (
                        <TablePagination
                            component="div" count={repaymentState.total}
                            page={repaymentState.page} onPageChange={handlePageChange('REPAYMENT')}
                            rowsPerPage={repaymentState.rowsPerPage} onRowsPerPageChange={handleRowsPerPageChange('REPAYMENT')}
                            rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton sx={paginationStyle}
                        />
                    )}
                </Box>
            </>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default UserActivity

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}